import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';
import { useBillingSummary, useUserPayments } from '../../queries/userPayments';
import { Transaction } from '../../queries/userPayments';

interface UserPaymentsTabProps {
  userId: string;
}

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
const CURRENCY_FORMATTER = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

function formatCents(cents: number): string {
  return CURRENCY_FORMATTER.format(cents / 100);
}

const STATUS_CLASS: Record<string, string> = {
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  succeeded: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  uncollectible: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  open: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status] ?? 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
}

const columns: DataTableColumn<Transaction>[] = [
  {
    key: 'date',
    header: 'Date',
    priority: true,
    render: (t) => DATE_FORMATTER.format(new Date(t.created_date)),
  },
  {
    key: 'type',
    header: 'Type',
    priority: false,
    render: (t) => (t.source_type === 'invoice' ? `Invoice ${t.invoice_number ?? ''}` : 'Charge'),
  },
  {
    key: 'status',
    header: 'Status',
    priority: true,
    render: (t) => <StatusBadge status={t.status} />,
  },
  {
    key: 'amount',
    header: 'Amount',
    priority: true,
    render: (t) => formatCents(Number(t.amount_paid ?? t.amount_due ?? 0)),
  },
  {
    key: 'link',
    header: '',
    priority: false,
    render: (t) =>
      t.link_url ? (
        <a href={t.link_url} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline">
          View
        </a>
      ) : null,
  },
];

export function UserPaymentsTab({ userId }: UserPaymentsTabProps) {
  const { data: summary, isLoading: summaryLoading } = useBillingSummary(userId);
  const { data: payments, isLoading: paymentsLoading } = useUserPayments(userId);

  return (
    <div className="space-y-4">
      {!summaryLoading && summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-3">
            <p className="text-xl font-bold">{formatCents(summary.total_paid_cents)}</p>
            <p className="text-xs text-muted-foreground">Total Paid</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <p className="text-xl font-bold">{summary.invoice_count}</p>
            <p className="text-xs text-muted-foreground">Paid Invoices</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <p className="text-xl font-bold">{summary.open_invoice_count}</p>
            <p className="text-xs text-muted-foreground">Open Invoices</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <p className="text-xl font-bold">
              {summary.last_payment_date ? DATE_FORMATTER.format(new Date(summary.last_payment_date)) : '—'}
            </p>
            <p className="text-xs text-muted-foreground">Last Payment</p>
          </div>
        </div>
      )}

      {paymentsLoading ? (
        <div className="py-8 text-center text-muted-foreground">Loading payments…</div>
      ) : (
        <DataTable
          columns={columns}
          data={payments?.transactions ?? []}
          getRowKey={(t) => `${t.source_type}-${t.id}`}
          emptyMessage="No payments found."
        />
      )}
    </div>
  );
}
